import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from '../hooks/useInView';

const FAQ = () => {
  const [ref, isInView] = useInView({ threshold: 0.1 });
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const faqs = [
    {
      question: 'Quels types de fenêtres réparez-vous à Toulouse ?',
      answer: (
        <>
          Nous réparons tous types de fenêtres : PVC, bois, aluminium, double vitrage, fenêtres oscillo-battantes, <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:underline cursor-pointer">baies vitrées coulissantes</a> et fixes. Nous intervenons également sur les <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:underline cursor-pointer">volets roulants</a> manuels et électriques.
        </>
      )
    },
    {
      question: 'Quel est le délai d\'intervention pour une réparation urgente ?',
      answer: (
        <>
          Pour les urgences (vitre cassée, problème de sécurité), nous intervenons dans les 24 heures à Toulouse et ses environs. Pour les réparations standard, le délai est généralement de 2 à 3 jours ouvrés. <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="text-teal-600 hover:underline cursor-pointer">Contactez-nous</a> pour plus d'informations.
        </>
      )
    },
    {
      question: 'Proposez-vous un devis gratuit pour la réparation de fenêtres ?',
      answer: (
        <>
          Oui, nous proposons systématiquement un <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="text-teal-600 hover:underline cursor-pointer">devis gratuit et sans engagement</a>. Nous nous déplaçons à votre domicile pour évaluer les travaux nécessaires et vous fournir un devis détaillé et transparent.
        </>
      )
    },
    {
      question: 'Quelle est la garantie sur vos réparations de fenêtres ?',
      answer: (
        <>
          Toutes nos interventions sont garanties 2 ans, pièces et main-d'œuvre. Cette garantie couvre les défauts de fabrication et les problèmes liés à l'installation. Nous utilisons uniquement des matériaux de qualité professionnelle. Découvrez <a href="#about" onClick={(e) => { e.preventDefault(); scrollToSection('about'); }} className="text-teal-600 hover:underline cursor-pointer">notre engagement qualité</a>.
        </>
      )
    },
    {
      question: 'Combien coûte une réparation de fenêtre à Toulouse ?',
      answer: (
        <>
          Le coût dépend du type de réparation nécessaire. Un remplacement de vitre simple commence à partir de 150€, tandis qu'une réparation de mécanisme coûte entre 100€ et 300€. <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="text-teal-600 hover:underline cursor-pointer">Contactez-nous pour un devis personnalisé gratuit</a>.
        </>
      )
    },
    {
      question: 'Intervenez-vous en dehors de Toulouse ?',
      answer: (
        <>
          Oui, nous intervenons dans toute la région toulousaine : Colomiers, Blagnac, Tournefeuille, Ramonville, Balma, L'Union, Cugnaux, Plaisance-du-Touch et toutes les communes de la Haute-Garonne. Consultez nos <a href="#zones" onClick={(e) => { e.preventDefault(); scrollToSection('zones'); }} className="text-teal-600 hover:underline cursor-pointer">zones d'intervention</a>.
        </>
      )
    },
    {
      question: 'Puis-je réparer ma fenêtre ou dois-je la remplacer ?',
      answer: (
        <>
          Dans la plupart des cas, une réparation est possible et plus économique qu'un remplacement complet. Nos <a href="#team" onClick={(e) => { e.preventDefault(); scrollToSection('team'); }} className="text-teal-600 hover:underline cursor-pointer">experts</a> évaluent l'état de votre fenêtre et vous conseillent la meilleure solution adaptée à votre situation et votre budget.
        </>
      )
    },
    {
      question: 'Quels sont vos horaires d\'intervention ?',
      answer: (
        <>
          Nous intervenons du lundi au samedi de 8h à 19h. Pour les urgences (sécurisation suite à effraction, vitre cassée), nous proposons un service d'astreinte disponible 24h/24 et 7j/7. <a href="tel:0554542545" className="text-teal-600 hover:underline cursor-pointer">Appelez-nous au 05 54 54 25 45</a>.
        </>
      )
    }
  ];

  return (
    <section id="faq" ref={ref} className="py-24 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Questions <strong className="text-teal-600">Fréquentes</strong>
          </h2>
          <p className="text-xl text-gray-600">
            Tout ce que vous devez savoir sur nos <a href="#services" onClick={(e) => { e.preventDefault(); scrollToSection('services'); }} className="text-teal-600 hover:underline cursor-pointer">services de réparation de fenêtres</a>
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.05 }}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 text-left flex items-center justify-between cursor-pointer"
              >
                <h4 className="text-lg font-semibold text-gray-900 pr-4">
                  {faq.question}
                </h4>
                <div className="w-8 h-8 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className={`ri-${openIndex === index ? 'subtract' : 'add'}-line text-teal-600 text-xl`}></i>
                </div>
              </button>
              {openIndex === index && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="px-6 pb-5"
                >
                  <p className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center mt-12"
        >
          <p className="text-lg text-gray-600 mb-6">
            Vous avez d'autres questions ? <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }} className="text-teal-600 hover:underline cursor-pointer font-semibold">N'hésitez pas à nous contacter !</a>
          </p>
          <a
            href="tel:0554542545"
            className="inline-block bg-gradient-to-r from-teal-500 to-teal-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-phone-line mr-2"></i>
            05 54 54 25 45
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;
